import { useMemo } from 'react';
import { usePolling } from './usePolling';
import { liveService } from '../services/liveService';
import { ConnectorStatusUI, mapEvseStatusToUI } from '../types/live';
import type { ChargePointStatusResponse } from '../types/live';

interface UseChargePointStatusOptions {
  chargePointId: string | null;
  enabled?: boolean;
}

interface UseChargePointStatusReturn {
  isOnline: boolean | undefined;
  evseStatuses: Map<string, { connectorId: string; status: ConnectorStatusUI; rawStatus: string; currentSoc: number | null }>;
  isConnected: boolean;
  lastUpdated: number | null;
}

export function useChargePointStatus(
  options: UseChargePointStatusOptions
): UseChargePointStatusReturn {
  const { chargePointId, enabled = true } = options;

  const { data } = usePolling<ChargePointStatusResponse>({
    fetcher: () => liveService.getChargePointStatus(chargePointId!),
    interval: 5_000,
    enabled: enabled && !!chargePointId,
  });

  const evseStatuses = useMemo(() => {
    const map = new Map<string, { connectorId: string; status: ConnectorStatusUI; rawStatus: string; currentSoc: number | null }>();
    if (data?.evses) {
      for (const evse of data.evses) {
        map.set(evse.evseId, {
          connectorId: evse.connectorId,
          status: mapEvseStatusToUI(evse.status),
          rawStatus: evse.status,
          currentSoc: evse.currentSoc ?? null,
        });
      }
    }
    return map;
  }, [data]);

  return {
    isOnline: data?.isOnline,
    evseStatuses,
    isConnected: !!data,
    lastUpdated: data ? Date.now() : null,
  };
}

export function useConnectorStatus(
  chargePointId: string | null,
  evseId: string | null
): ConnectorStatusUI | undefined {
  const { evseStatuses } = useChargePointStatus({
    chargePointId,
    enabled: !!chargePointId && !!evseId,
  });
  return evseId ? evseStatuses.get(evseId)?.status : undefined;
}

export function useHasAvailableConnector(chargePointId: string | null): boolean {
  const { evseStatuses } = useChargePointStatus({ chargePointId });
  return useMemo(() => {
    for (const { status } of evseStatuses.values()) {
      if (status === 'available') return true;
    }
    return false;
  }, [evseStatuses]);
}
